/* ============================================================
   The world of the CafeVista business story.
   One SVG scene graph, six stages. Every stage lives in the same
   coordinate space and is cross-faded in place by BusinessStory,
   so the camera travels through one continuous world rather than
   cutting between separate illustrations.

   Coordinate space: 1200 x 760, ground horizon at y = 470.
   ============================================================ */
import React from "react";
import { motion, useTransform } from "framer-motion";

export const VIEW = { w: 1200, h: 760, horizon: 470, cx: 600 };

/* ---------- tiny motion-value helpers ---------- */
export function useLerp(t, from, to) {
  return useTransform(t, [0, 1], [from, to]);
}

/* Re-maps a 0..1 driver onto a sub-window, clamped. */
export function useWindow(t, start, end) {
  return useTransform(t, [start, end], [0, 1], { clamp: true });
}

/* ---------- shared defs: gradients + masks ---------- */
export function WorldDefs() {
  return (
    <defs>
      <linearGradient id="cvSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0a1024" />
        <stop offset="55%" stopColor="#0b1124" />
        <stop offset="100%" stopColor="#070a16" />
      </linearGradient>

      <radialGradient id="cvAura" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stopColor="#34e6ff" stopOpacity="0.20" />
        <stop offset="60%" stopColor="#4f8cff" stopOpacity="0.06" />
        <stop offset="100%" stopColor="#4f8cff" stopOpacity="0" />
      </radialGradient>

      <radialGradient id="cvWarm" cx="50%" cy="70%" r="60%">
        <stop offset="0%" stopColor="#e7b888" stopOpacity="0.22" />
        <stop offset="70%" stopColor="#c98a52" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#c98a52" stopOpacity="0" />
      </radialGradient>

      <linearGradient id="cvPanel" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#16203c" />
        <stop offset="100%" stopColor="#0c1226" />
      </linearGradient>

      <linearGradient id="cvEdge" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#34e6ff" />
        <stop offset="100%" stopColor="#4f8cff" />
      </linearGradient>

      <linearGradient id="cvPaper" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f4f7ff" />
        <stop offset="100%" stopColor="#c9d4ec" />
      </linearGradient>

      <linearGradient id="cvScan" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#34e6ff" stopOpacity="0" />
        <stop offset="45%" stopColor="#34e6ff" stopOpacity="0.5" />
        <stop offset="55%" stopColor="#79f0ff" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#34e6ff" stopOpacity="0" />
      </linearGradient>

      <linearGradient id="cvGrowth" x1="0" y1="1" x2="1" y2="0">
        <stop offset="0%" stopColor="#34e6ff" />
        <stop offset="55%" stopColor="#79f0ff" />
        <stop offset="100%" stopColor="#e7b888" />
      </linearGradient>

      {/* fades the ground grid out toward the horizon */}
      <linearGradient id="cvFloorFade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="35%" stopColor="#ffffff" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
      </linearGradient>
      <linearGradient id="cvScanFade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="22%" stopColor="#ffffff" stopOpacity="1" />
        <stop offset="78%" stopColor="#ffffff" stopOpacity="1" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <mask id="cvScanMask">
        <rect x="-400" y="90" width="2000" height="330" fill="url(#cvScanFade)" />
      </mask>
      <mask id="cvFloorMask">
        <rect x="-900" y="440" width={VIEW.w + 1800} height={VIEW.h - 440} fill="url(#cvFloorFade)" />
      </mask>
    </defs>
  );
}

/* Distant city, standing on the horizon. Lives in the backdrop rather than
   the camera group so it stays welded to the horizon line and the ground
   grid, which are not transformed. */
const SKYLINE = [
  [120, 54, 92], [186, 40, 138], [236, 66, 70], [312, 46, 116], [368, 58, 158],
  [438, 38, 88], [730, 50, 124], [792, 62, 84], [866, 42, 148], [920, 56, 104],
  [988, 44, 132], [1044, 60, 76],
];

/* ---------- persistent backdrop: sky, horizon, skyline, ground grid ---------- */
export function Backdrop({ gridOpacity, auraOpacity, warmOpacity, horizonGlow, skylineT }) {
  const verticals = [];
  for (let i = -14; i <= 14; i += 1) {
    verticals.push(
      <line
        key={"v" + i}
        x1={VIEW.cx + i * 6}
        y1={VIEW.horizon}
        x2={VIEW.cx + i * 130}
        y2={VIEW.h}
      />
    );
  }
  const horizontals = [];
  for (let i = 1; i <= 10; i += 1) {
    const y = VIEW.horizon + Math.pow(i / 10, 2.1) * (VIEW.h - VIEW.horizon);
    horizontals.push(<line key={"h" + i} x1="-900" y1={y} x2={VIEW.w + 900} y2={y} />);
  }

  return (
    <g>
      <rect x="-900" y="-200" width={VIEW.w + 1800} height={VIEW.h + 400} fill="url(#cvSky)" />
      <motion.rect
        x="-900"
        y="-200"
        width={VIEW.w + 1800}
        height={VIEW.h + 400}
        fill="url(#cvAura)"
        style={{ opacity: auraOpacity }}
      />
      <motion.rect
        x="-900"
        y="-200"
        width={VIEW.w + 1800}
        height={VIEW.h + 400}
        fill="url(#cvWarm)"
        style={{ opacity: warmOpacity }}
      />

      <motion.g style={{ opacity: skylineT }}>
        {SKYLINE.map((b, i) => (
          <g key={i}>
            <rect x={b[0]} y={VIEW.horizon - b[2]} width={b[1]} height={b[2]} rx="2" fill="#131c36" />
            {Array.from({ length: Math.max(1, Math.floor(b[2] / 22)) }).map((_, r) => (
              <rect
                key={r}
                x={b[0] + 5}
                y={VIEW.horizon - b[2] + 8 + r * 20}
                width={b[1] - 10}
                height="5"
                rx="2"
                fill="#34e6ff"
                opacity={0.1 + ((i + r) % 4) * 0.05}
              />
            ))}
          </g>
        ))}
      </motion.g>

      <motion.rect
        x="-900"
        y={VIEW.horizon - 1}
        width={VIEW.w + 1800}
        height="2"
        fill="url(#cvEdge)"
        style={{ opacity: horizonGlow }}
      />

      <motion.g
        mask="url(#cvFloorMask)"
        stroke="#4f8cff"
        strokeWidth="1"
        style={{ opacity: gridOpacity }}
      >
        {verticals}
        {horizontals}
      </motion.g>
    </g>
  );
}

/* ============================================================
   STAGE 1 - the business challenge arrives
   ============================================================ */
export function StageBrief({ enter }) {
  const lines = [0, 1, 2, 3, 4, 5];
  return (
    <motion.g style={{ y: enter }}>
      <g transform="translate(600 300) rotate(-7)">
        <rect x="-96" y="-136" width="180" height="250" rx="8" fill="#1a2440" opacity="0.5" transform="rotate(6)" />
        <rect x="-100" y="-134" width="180" height="250" rx="8" fill="#26314f" opacity="0.7" transform="rotate(3)" />

        <rect x="-104" y="-132" width="180" height="250" rx="8" fill="url(#cvPaper)" />
        <rect x="-104" y="-132" width="180" height="34" rx="8" fill="#0b1124" opacity="0.92" />
        <text x="-88" y="-110" fontSize="13" fill="#79f0ff" letterSpacing="2.6" fontWeight="600">BRIEF</text>

        {lines.map((i) => (
          <rect
            key={i}
            x="-86"
            y={-78 + i * 22}
            width={i % 3 === 2 ? 92 : 140}
            height="7"
            rx="3.5"
            fill="#8496b8"
            opacity={0.85 - i * 0.06}
          />
        ))}

        {/* the problem: a line trending the wrong way */}
        <path
          d="M-86 74 L-44 88 L-8 78 L26 100 L54 94"
          stroke="#ff7a8a"
          strokeWidth="3.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="54" cy="94" r="5" fill="#ff7a8a" />
      </g>

      {[[430, 190], [790, 250], [455, 400], [772, 405]].map((pos, i) => (
        <motion.g
          key={i}
          animate={{ y: [0, -9, 0], opacity: [0.35, 0.7, 0.35] }}
          transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
        >
          <circle cx={pos[0]} cy={pos[1]} r="17" fill="none" stroke="#ff7a8a" strokeOpacity="0.4" />
          <text x={pos[0]} y={pos[1] + 6} textAnchor="middle" fontSize="18" fill="#ff9ca6" fontWeight="600">?</text>
        </motion.g>
      ))}
    </motion.g>
  );
}

/* ============================================================
   STAGE 2 - discovery: inputs sorted, connected, clarified
   ============================================================ */
/* Scatter positions stay right of x=430: further left and a chip drifts under
   the narration column while the reader is still on it. */
const CHIP_SCATTER = [
  [452, 168], [548, 128], [648, 148], [786, 190], [872, 288],
  [472, 296], [438, 402], [536, 442], [640, 458], [762, 428],
  [858, 384], [706, 108], [610, 96], [890, 168],
];
const CHIP_SORTED = [
  [452, 178], [452, 226], [452, 274], [452, 322], [452, 370],
  [600, 178], [600, 226], [600, 274], [600, 322],
  [748, 178], [748, 226], [748, 274], [748, 322], [748, 370],
];
const COLUMN_LABELS = [
  [452, 138, "SYSTEMS"],
  [600, 138, "PROCESS"],
  [748, 138, "DATA"],
];

export function StageDiscovery({ sortT, scanX, linkLength }) {
  return (
    <g>
      {COLUMN_LABELS.map((c) => (
        <motion.text
          key={c[2]}
          x={c[0]}
          y={c[1]}
          textAnchor="middle"
          fontSize="12"
          letterSpacing="2.4"
          fontWeight="600"
          fill="#79f0ff"
          style={{ opacity: sortT }}
        >
          {c[2]}
        </motion.text>
      ))}

      <g stroke="#34e6ff" strokeOpacity="0.45" strokeWidth="1.4" fill="none">
        <motion.path d="M452 190 L600 238 L748 190" style={{ pathLength: linkLength }} />
        <motion.path d="M452 286 L600 286 L748 286" style={{ pathLength: linkLength }} />
        <motion.path d="M452 334 L600 382 L748 334" style={{ pathLength: linkLength }} />
      </g>

      {CHIP_SCATTER.map((s, i) => (
        <ChipMorph key={i} sx={s[0]} sy={s[1]} tx={CHIP_SORTED[i][0]} ty={CHIP_SORTED[i][1]} t={sortT} index={i} />
      ))}

      <motion.rect
        y="90" width="150" height="330" fill="url(#cvScan)"
        mask="url(#cvScanMask)"
        style={{ x: scanX, opacity: sortT }}
      />
    </g>
  );
}

function ChipMorph({ sx, sy, tx, ty, t, index }) {
  const x = useLerp(t, sx, tx);
  const y = useLerp(t, sy, ty);
  const dot = index % 3 === 0 ? "#79f0ff" : index % 3 === 1 ? "#e7b888" : "#4f8cff";
  return (
    <motion.g style={{ x, y }}>
      <rect x="-38" y="-11" width="76" height="22" rx="6" fill="#16203c" stroke="#34e6ff" strokeOpacity="0.5" />
      <circle cx="-25" cy="0" r="4" fill={dot} />
      <rect x="-14" y="-3.5" width="42" height="7" rx="3.5" fill="#8496b8" opacity="0.7" />
    </motion.g>
  );
}

/* ============================================================
   STAGE 3 - engineering the solution
   ============================================================ */
const MODULES = [
  { x: 600, y: 190, w: 300, h: 54, label: "DATA LAYER", dx: 0, dy: -280 },
  { x: 460, y: 272, w: 172, h: 54, label: "WORKFLOW", dx: -340, dy: 0 },
  { x: 740, y: 272, w: 172, h: 54, label: "AUTOMATION", dx: 340, dy: 0 },
  { x: 460, y: 354, w: 172, h: 54, label: "INTEGRATION", dx: -340, dy: 60 },
  { x: 740, y: 354, w: 172, h: 54, label: "INTERFACE", dx: 340, dy: 60 },
  { x: 600, y: 436, w: 300, h: 54, label: "INTELLIGENCE", dx: 0, dy: 280 },
];

export function StageEngineering({ assembleT, frameLength }) {
  return (
    <g>
      <g stroke="#4f8cff" strokeOpacity="0.55" strokeWidth="1.6" fill="none">
        <motion.rect x="428" y="152" width="344" height="326" rx="14" style={{ pathLength: frameLength }} />
        <motion.path
          d="M428 244 L772 244 M428 326 L772 326 M428 408 L772 408 M600 152 L600 478"
          strokeOpacity="0.25"
          style={{ pathLength: frameLength }}
        />
      </g>

      {[[428, 152, 1, 1], [772, 152, -1, 1], [428, 478, 1, -1], [772, 478, -1, -1]].map((c, i) => (
        <motion.path
          key={i}
          d={"M" + c[0] + " " + (c[1] + c[3] * 26) + " L" + c[0] + " " + c[1] + " L" + (c[0] + c[2] * 26) + " " + c[1]}
          stroke="#34e6ff"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          style={{ pathLength: frameLength }}
        />
      ))}

      {MODULES.map((m, i) => (
        <ModuleBlock key={m.label} m={m} t={assembleT} index={i} />
      ))}
    </g>
  );
}

function ModuleBlock({ m, t, index }) {
  const start = index * 0.1;
  const local = useWindow(t, start, start + 0.45);
  const x = useLerp(local, m.x + m.dx, m.x);
  const y = useLerp(local, m.y + m.dy, m.y);
  return (
    <motion.g style={{ x, y, opacity: local }}>
      <rect x={-m.w / 2} y={-m.h / 2} width={m.w} height={m.h} rx="9" fill="url(#cvPanel)" stroke="#34e6ff" strokeOpacity="0.55" />
      <rect x={-m.w / 2} y={-m.h / 2} width="4" height={m.h} rx="2" fill="url(#cvEdge)" />
      <text x={-m.w / 2 + 20} y="5" fontSize="13" letterSpacing="2" fontWeight="600" fill="#eaf0fb">
        {m.label}
      </text>
      <circle cx={m.w / 2 - 20} cy="0" r="4" fill="#34e6ff" />
    </motion.g>
  );
}

/* ============================================================
   STAGE 4 - the solution as one integrated system
   ============================================================ */
const BARS = [38, 62, 48, 78, 66, 88, 72, 94];
const KPIS = [["THROUGHPUT", "+34%"], ["CYCLE TIME", "-41%"], ["ACCURACY", "99.4%"]];
const FLOW_STEPS = ["Order", "Plan", "Make", "Ship", "Bill"];

export function StageSolution({ liveT, pulse }) {
  return (
    <g>
      <rect x="418" y="142" width="364" height="346" rx="18" fill="url(#cvPanel)" stroke="#34e6ff" strokeOpacity="0.5" />
      <rect x="418" y="142" width="364" height="44" rx="18" fill="#0b1124" opacity="0.75" />
      <rect x="418" y="182" width="364" height="1" fill="#34e6ff" opacity="0.3" />
      <circle cx="442" cy="164" r="4.5" fill="#ff7a8a" opacity="0.7" />
      <circle cx="458" cy="164" r="4.5" fill="#e7b888" opacity="0.7" />
      <circle cx="474" cy="164" r="4.5" fill="#34e6ff" opacity="0.9" />
      <text x="600" y="169" textAnchor="middle" fontSize="12" letterSpacing="2.6" fontWeight="600" fill="#79f0ff">
        ONE INTEGRATED SYSTEM
      </text>

      {KPIS.map((k, i) => (
        <g key={k[0]} transform={"translate(" + (452 + i * 116) + " 214)"}>
          <text fontSize="9.5" letterSpacing="1.6" fill="#6c7c98">{k[0]}</text>
          <motion.text y="26" fontSize="21" fontWeight="700" fill={i === 1 ? "#e7b888" : "#79f0ff"} style={{ opacity: liveT }}>
            {k[1]}
          </motion.text>
        </g>
      ))}

      <g transform="translate(452 268)">
        <rect x="-6" y="-4" width="308" height="96" rx="8" fill="#0b1124" opacity="0.5" />
        {BARS.map((h, i) => (
          <Bar key={i} x={i * 37} h={h} t={liveT} />
        ))}
      </g>

      <g transform="translate(446 386)">
        {FLOW_STEPS.map((s, i) => (
          <g key={s} transform={"translate(" + i * 76 + " 0)"}>
            <motion.circle cx="14" cy="14" r="11" fill="#16203c" stroke="#34e6ff" strokeOpacity="0.6" style={{ opacity: liveT }} />
            <motion.circle cx="14" cy="14" r="4" fill="#34e6ff" style={{ opacity: pulse }} />
            <text x="14" y="42" textAnchor="middle" fontSize="10" fill="#a7b6cf">{s}</text>
            {i < FLOW_STEPS.length - 1 && (
              <motion.rect x="27" y="12.5" width="48" height="3" rx="1.5" fill="#34e6ff" fillOpacity="0.4" style={{ opacity: liveT }} />
            )}
          </g>
        ))}
      </g>

      {/* the assistant, docked into the system rather than floating free */}
      <g transform="translate(600 452)">
        <rect x="-152" y="-14" width="304" height="30" rx="15" fill="#0b1124" stroke="#34e6ff" strokeOpacity="0.35" />
        <motion.circle cx="-134" cy="1" r="5" fill="#79f0ff" style={{ opacity: pulse }} />
        <text x="-118" y="5" fontSize="11" fill="#a7b6cf">
          &ldquo;What is late this week?&rdquo; &mdash; answered from live data
        </text>
      </g>
    </g>
  );
}

function Bar({ x, h, t }) {
  const height = useLerp(t, 3, h);
  const y = useLerp(t, 85, 88 - h);
  return <motion.rect x={x} width="24" rx="4" fill="url(#cvEdge)" style={{ height, y }} />;
}

/* ============================================================
   STAGE 5 - integration into business functions
   ============================================================ */
/* Six business functions.

   Landscape: 3 x 2, parked clear of the narration column on the left and the
   progress rail on the right, so the grid centres near x = 800.
   Portrait: 2 x 3 centred on x = 600, because three 138-wide columns cannot
   fit a phone at a readable scale. */
const GRID = {
  landscape: { cols: [640, 800, 960], rows: [300, 424], hub: { x: 800, y: 236 }, top: 132 },
  portrait: { cols: [524, 676], rows: [284, 400, 516], hub: { x: 600, y: 236 }, top: 128 },
};
const BOX = { w: 138, h: 104 };

export function grid(portrait) {
  return portrait ? GRID.portrait : GRID.landscape;
}

function slot(i, g) {
  const n = g.cols.length;
  return { x: g.cols[i % n], y: g.rows[Math.floor(i / n)] };
}

export function StageIntegration({ areas, dropT, wireLength, portrait }) {
  const g = grid(portrait);
  const hubBottom = g.top + 76;
  return (
    <g>
      <g>
        <rect x={g.hub.x - 140} y={g.top} width="280" height="76" rx="14" fill="url(#cvPanel)" stroke="#34e6ff" strokeOpacity="0.6" />
        <rect x={g.hub.x - 140} y={g.top} width="280" height="3" rx="1.5" fill="url(#cvEdge)" />
        <text x={g.hub.x} y={g.top + 34} textAnchor="middle" fontSize="12.5" letterSpacing="2.4" fontWeight="600" fill="#eaf0fb">
          THE SOLUTION
        </text>
        <text x={g.hub.x} y={g.top + 54} textAnchor="middle" fontSize="10.5" fill="#6c7c98">
          data · workflow · automation · intelligence
        </text>
      </g>

      {areas.map((a, i) => {
        const s = slot(i, g);
        const top = s.y - BOX.h / 2;
        return (
          <g key={a.id}>
            <motion.path
              d={"M" + g.hub.x + " " + hubBottom + " C " + g.hub.x + " " + (g.hub.y + 20) + ", " + s.x + " " + (top - 46) + ", " + s.x + " " + top}
              stroke="#34e6ff"
              strokeOpacity="0.5"
              strokeWidth="1.8"
              fill="none"
              style={{ pathLength: wireLength }}
            />
            <Pillar cx={s.x} cy={s.y} label={a.label} detail={a.detail} t={dropT} index={i} />
          </g>
        );
      })}
    </g>
  );
}

function Pillar({ cx, cy, label, detail, t, index }) {
  const start = index * 0.09;
  const local = useWindow(t, start, start + 0.5);
  const y = useLerp(local, 44, 0);
  const top = cy - BOX.h / 2;
  return (
    <motion.g style={{ x: cx, y, opacity: local }}>
      <rect x={-BOX.w / 2} y={top} width={BOX.w} height={BOX.h} rx="12" fill="url(#cvPanel)" stroke="#4f8cff" strokeOpacity="0.5" />
      <rect x={-BOX.w / 2} y={top} width={BOX.w} height="3" rx="1.5" fill="url(#cvEdge)" />
      <text y={top + 30} textAnchor="middle" fontSize="12.5" fontWeight="600" fill="#eaf0fb">{label}</text>
      <foreignObject x={-BOX.w / 2 + 8} y={top + 40} width={BOX.w - 16} height={BOX.h - 46}>
        <div className="cv-bstory__pilltext">{detail}</div>
      </foreignObject>
      <motion.circle
        cy={top}
        r="5"
        fill="#34e6ff"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 2.4, repeat: Infinity, delay: index * 0.2 }}
      />
    </motion.g>
  );
}

/* ============================================================
   STAGE 6 - the Vista: the business running better
   ============================================================ */
export function StageVista({ areas, flowT, growthLength, portrait }) {
  const g = grid(portrait);
  const n = g.cols.length;
  const step = g.cols.length > 1 ? g.cols[1] - g.cols[0] : 160;
  return (
    <g>
      {areas.map((a, i) => {
        const s = slot(i, g);
        const top = s.y - BOX.h / 2;
        const lastInRow = i % n === n - 1;
        return (
          <g key={a.id}>
            <rect x={s.x - BOX.w / 2} y={top} width={BOX.w} height={BOX.h} rx="12" fill="url(#cvPanel)" stroke="#34e6ff" strokeOpacity="0.45" />
            <rect x={s.x - BOX.w / 2} y={top} width={BOX.w} height="3" rx="1.5" fill="url(#cvEdge)" />
            <text x={s.x} y={top + 28} textAnchor="middle" fontSize="11.5" fontWeight="600" fill="#eaf0fb">{a.label}</text>
            {[0, 1, 2].map((r) => (
              <MeterBar key={r} x={s.x - 48} y={top + 44 + r * 15} to={96 - r * 22} o={0.25 + r * 0.18} t={flowT} />
            ))}
            {!lastInRow && <FlowLink x1={s.x + BOX.w / 2} x2={s.x + step - BOX.w / 2} y={s.y} t={flowT} index={i} />}
          </g>
        );
      })}

      {/* growth curve rising over the running business */}
      <motion.path
        d={portrait
          ? "M418 268 C 500 258, 540 214, 600 186 S 720 148, 784 128"
          : "M400 322 C 600 314, 690 240, 812 192 S 998 132, 1090 96"}
        stroke="url(#cvGrowth)"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        style={{ pathLength: growthLength }}
      />
      <motion.g style={{ opacity: flowT }}>
        <circle cx={portrait ? 784 : 1090} cy={portrait ? 128 : 96} r="8" fill="#e7b888" />
        <circle cx={portrait ? 784 : 1090} cy={portrait ? 128 : 96} r="18" fill="none" stroke="#e7b888" strokeOpacity="0.4" />
      </motion.g>
    </g>
  );
}

function MeterBar({ x, y, to, o, t }) {
  const width = useLerp(t, 6, to);
  return <motion.rect x={x} y={y} height="6" rx="3" fill="#34e6ff" fillOpacity={o} style={{ width }} />;
}

function FlowLink({ x1, x2, y, t, index }) {
  const width = useLerp(t, 0, x2 - x1);
  return (
    <g>
      <motion.rect x={x1} y={y - 1.25} height="2.5" rx="1.25" fill="#34e6ff" fillOpacity="0.3" style={{ width }} />
      <motion.circle
        cy={y}
        r="3.5"
        fill="#79f0ff"
        animate={{ cx: [x1, x2], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, delay: index * 0.35, ease: "linear" }}
      />
    </g>
  );
}
